import { X } from "lucide-react-native";
import { FC, ReactNode, useEffect } from "react";
import { Modal, ScrollView } from "react-native";

import { Box, HStack, Pressable, Text, VStack } from "@/components/ui";

interface SheetProps {
  children: ReactNode;
  /** Pinned under the scrolling body (actions). */
  footer?: ReactNode;
  onClose: () => void;
  open: boolean;
  subtitle?: string;
  title: string;
}

/** Right-hand sheet used by the tracker's sources, method and wave panels. */
const Sheet: FC<SheetProps> = ({
  children,
  footer,
  onClose,
  open,
  subtitle,
  title,
}) => {
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <Modal animationType="fade" onRequestClose={onClose} transparent visible>
      <Pressable className="flex-1 flex-row bg-black/40" onPress={onClose}>
        <Box className="flex-1" />
        <Pressable
          className="w-full max-w-[640px] h-full bg-background-0 shadow-hard-5"
          onPress={(e) => e.stopPropagation()}
        >
          <VStack className="flex-1">
            <HStack className="items-start justify-between px-6 pt-6 pb-4 border-b border-outline-200">
              <VStack className="flex-1 gap-0.5 pr-4">
                <Text className="text-lg font-semibold text-typography-900">
                  {title}
                </Text>
                {subtitle && (
                  <Text className="text-xs text-typography-500">
                    {subtitle}
                  </Text>
                )}
              </VStack>
              <Pressable
                className="p-1 rounded-md hover:bg-background-100"
                onPress={onClose}
              >
                <X color="rgb(var(--color-typography-500))" size={18} />
              </Pressable>
            </HStack>
            <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
              <VStack className="gap-6 px-6 py-6">{children}</VStack>
            </ScrollView>
            {footer && (
              <Box className="px-6 py-4 border-t border-outline-200">
                {footer}
              </Box>
            )}
          </VStack>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default Sheet;
